import { useState } from "react";
import { Send, CheckCircle2, Mail, Phone, Globe } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { TOOLS, ORG } from "@/data/tools";
import { Reveal } from "@/components/Reveal";
import { AutoTextarea, Pills, TextField } from "@/components/tools/fields";

const ROLES = ["SENDCO", "Inclusion Lead", "Headteacher", "School Leader", "Governor", "Teacher or teaching assistant", "Parent or carer", "Other"];
const SECTIONS = ["Welcome", ...TOOLS.map((t) => t.title)];
const FOUNDING = ["Yes", "No", "Tell me more"].map((v) => ({ v, label: v }));
const INIT = { school: "", role: "", most_useful: "", least_useful: "", build_next: "", founding: "", email: "", website: "" };

const Select = ({ id, label, value, onChange, options, placeholder, testid }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="field-label">{label}</label>
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className="field-input" data-testid={testid}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </div>
);

const StayConnected = () => (
  <div className="mt-10 rounded-[2rem] bg-brand p-7 text-brand-fg sm:p-8" data-testid="stay-connected">
    <h3 className="font-display text-3xl font-medium">Contact us</h3>
    <p className="mt-2 opacity-90">We would genuinely value your feedback.</p>
    <ul className="mt-5 space-y-3">
      <li className="flex gap-2.5"><Mail size={18} className="mt-0.5 shrink-0" aria-hidden /><a href={`mailto:${ORG.director}`} className="break-all font-bold">{ORG.director}</a></li>
      <li className="flex gap-2.5"><Mail size={18} className="mt-0.5 shrink-0" aria-hidden /><a href={`mailto:${ORG.datalead}`} className="break-all font-bold">{ORG.datalead}</a></li>
      <li className="flex gap-2.5"><Phone size={18} className="mt-0.5 shrink-0" aria-hidden /><a href={`tel:${ORG.phone.replace(/\s/g, "")}`} className="font-bold">{ORG.phone}</a></li>
      <li className="flex gap-2.5"><Globe size={18} className="mt-0.5 shrink-0" aria-hidden /><a href={`https://${ORG.web}`} target="_blank" rel="noopener noreferrer" className="break-all font-bold">{ORG.web}</a></li>
    </ul>
  </div>
);

export const Feedback = () => {
  const [f, setF] = useState(INIT);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.role || !(f.most_useful || f.least_useful || f.build_next.trim())) {
      setError("Please choose your role and answer at least one question.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await api.post("/feedback", { ...f, email: f.email || null });
      setStatus("sent");
      setF(INIT);
    } catch (err) {
      setError(errorText(err));
      setStatus("idle");
    }
  };
  return (
    <section id="feedback" aria-labelledby="feedback-title" className="py-24 lg:py-36">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Feedback · No sign-up</p>
          <h2 id="feedback-title" className="font-display text-4xl font-medium leading-[1.02] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            We'd love <span className="italic text-brand">your feedback.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-ink-soft sm:text-lg">
            Thank you for using this resource. Your feedback helps improve future toolkits, dashboards and resources. Your email is optional and only needed if you would like a reply.
          </p>
          <StayConnected />
        </Reveal>
        <Reveal delay={0.1} className="rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-10">
          {status === "sent" ? (
            <div role="status" className="flex flex-col items-start gap-4 py-10" data-testid="feedback-success">
              <CheckCircle2 size={44} className="text-brand" aria-hidden />
              <h3 className="font-display text-3xl font-medium">Thank you. Your feedback has been sent.</h3>
              <p className="text-ink-soft">We read every response.</p>
              <button type="button" className="btn btn-ghost" onClick={() => setStatus("idle")} data-testid="feedback-again">Send more feedback</button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-6" data-testid="feedback-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField id="fb-school" label="School name" autoComplete="organization" value={f.school} onChange={set("school")} testid="feedback-school" />
                <Select id="fb-role" label="Role" value={f.role} onChange={set("role")} options={ROLES} placeholder="Choose your role" testid="feedback-role" />
                <Select id="fb-most" label="Which section was most useful?" value={f.most_useful} onChange={set("most_useful")} options={SECTIONS} placeholder="Choose a section" testid="feedback-most" />
                <Select id="fb-least" label="Which section was least useful?" value={f.least_useful} onChange={set("least_useful")} options={SECTIONS} placeholder="Choose a section" testid="feedback-least" />
              </div>
              <AutoTextarea id="fb-next" label="What additional resources would help?" rows={4} value={f.build_next} onChange={set("build_next")} testid="feedback-build-next" />
              <Pills legend="Would you be interested in a funded school place with access to future dashboards and resources?" hideLegend={false} name="fb-founding"
                options={FOUNDING} value={f.founding} onChange={set("founding")} testid="feedback-founding" />
              <TextField id="fb-email" label="Email (optional, for a reply)" type="email" autoComplete="email" value={f.email} onChange={set("email")} testid="feedback-email" />
              <div className="hidden" aria-hidden="true">
                <label htmlFor="fb-website">Website</label>
                <input id="fb-website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} />
              </div>
              {error && <p role="alert" className="rounded-xl border-2 p-3 font-bold" style={{ borderColor: "var(--rag-red-fg)", color: "var(--rag-red-fg)", background: "var(--rag-red)" }} data-testid="feedback-error">{error}</p>}
              <button type="submit" disabled={status === "sending"} className="btn btn-brand text-base disabled:opacity-70" data-testid="feedback-submit">
                <Send size={16} aria-hidden /> {status === "sending" ? "Sending…" : "Send feedback"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
